import { IconTypes } from '@/components/Icon'
import { DropdownItem } from '@/components/ButtonDropdown'
import {
  CategoryConfig,
  FileActionType,
  FileDropdownActionType,
  FileOrderOption,
  FileTypes,
  FolderActionType,
  KnownMimeType,
} from '@/lib/types/FilesT'
import { MapperType } from '@/lib/types/utilsT'

export type FileType = MapperType<FileTypes>
export const FILE_TYPES = {
  FILE: 'file',
  FOLDER: 'folder',
} satisfies FileType

export const FILES_ORDER_OPTIONS: FileOrderOption[] = [
  { value: 'name', title: 'Name (A–Z)', orderDirection: 'asc' },
  { value: 'created_at', title: 'Date added (Newest)', orderDirection: 'desc' },
  { value: 'size', title: 'File size (Largest)', orderDirection: 'desc' },
]

export const FILE_OPTIONS_ADD: DropdownItem<FileDropdownActionType>[] = [
  {
    label: 'New Folder',
    value: 'new_folder',
    icon: 'folder-add',
    classInner: 'border-b border-n-3 dark:border-n-5 pb-1 mb-1',
    classIcon: 'fill-n-4',
  },
  {
    label: 'Upload Files',
    value: 'upload_files',
    icon: 'file-upload',
    classIcon: 'fill-n-4',
  },
]

export const MIME_ICON_MAP: Record<KnownMimeType | string, IconTypes> = {
  // PDFs
  'application/pdf': 'file',

  // Word
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',

  // Excel
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',

  // PowerPoint
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',

  // Text
  'text/plain': 'document',
  'text/csv': 'csv-file',

  // Archives
  'application/zip': 'archive',
  'application/x-rar-compressed': 'archive',
  'application/x-7z-compressed': 'archive',

  // Audio
  'audio/mpeg': 'audio',
  'audio/wav': 'audio',

  // Video
  'video/mp4': 'video-file',
  'video/x-msvideo': 'video-file',
  'application/mp4': 'video-file',

  // / vCard
  'text/vcard': 'vcf-file',
  'text/x-vcard': 'vcf-file',
  'application/vcard': 'vcf-file',
  'application/x-vcard': 'vcf-file',

  // Executables / binaries
  'application/x-msdownload': 'exe-file',
  'application/vnd.microsoft.portable-executable': 'exe-file',
  'application/x-msdos-program': 'exe-file',
  'application/octet-stream': 'exe-file',
}

export const FILE_TYPE_CATEGORIES = {
  IMAGE: 'image',
  PDF: 'pdf',
  WORD: 'word',
  EXCEL: 'excel',
  TEXT: 'text',
  AUDIO: 'audio',
  VIDEO: 'video',
  ARCHIVE: 'archive',
  OTHER: 'other',
  PPT: 'ppt',
} as const

export const FOLDER_ACTIONS: DropdownItem<FolderActionType>[] = [
  {
    label: 'Share',
    value: 'share',
    icon: 'share-1' as IconTypes,
    classIcon: 'w-3 h-3 fill-n-3',
    className: 'hidden sm:block',
  },
  {
    label: 'Change name',
    value: 'update_name',
    classIcon: 'w-3 h-3 fill-n-3',
    icon: 'edit' as IconTypes,
  },
  {
    label: 'Delete',
    value: 'delete',
    classIcon: 'w-3.5 h-3.5 fill-red-500 dark:fill-red-400',
    icon: 'trash' as IconTypes,
  },
]

export const FILE_ACTIONS: DropdownItem<FileActionType>[] = [
  {
    label: 'Share',
    value: 'share',
    icon: 'share-1' as IconTypes,
    classIcon: 'w-3 h-3 fill-n-4 dark:fill-n-3',
    className: 'hidden sm:block',
  },
  {
    label: 'Replace',
    value: 'replace',
    icon: 'refresh' as IconTypes,
    classIcon: 'w-3 h-3 fill-n-4 dark:fill-n-3',
  },
  {
    label: 'Delete',
    value: 'delete',
    classIcon: 'w-3.5 h-3.5 fill-red-500 dark:fill-red-400',
    icon: 'trash' as IconTypes,
  },
]

export const CATEGORY_MAP: Record<string, CategoryConfig> = {
  [FILE_TYPE_CATEGORIES.IMAGE]: {
    bg: 'from-blue-500/10 to-purple-500/10',
    iconFill: 'fill-blue-500',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
    label: 'Image',
  },
  [FILE_TYPE_CATEGORIES.PDF]: {
    bg: 'from-red-500/10 to-orange-400/10',
    iconFill: 'fill-red-500',
    badge: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-400',
    label: 'PDF',
  },
  [FILE_TYPE_CATEGORIES.WORD]: {
    bg: 'from-blue-600/10 to-sky-400/10',
    iconFill: 'fill-blue-600',
    badge: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400',
    label: 'Word',
  },
  [FILE_TYPE_CATEGORIES.EXCEL]: {
    bg: 'from-green-600/10 to-emerald-400/10',
    iconFill: 'fill-green-600',
    badge: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400',
    label: 'Excel',
  },
  [FILE_TYPE_CATEGORIES.PPT]: {
    bg: 'from-orange-500/10 to-red-400/10',
    iconFill: 'fill-orange-500',
    badge: 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
    label: 'Presentation',
  },
  [FILE_TYPE_CATEGORIES.AUDIO]: {
    bg: 'from-purple-600/10 to-pink-400/10',
    iconFill: 'fill-purple-500',
    badge: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
    label: 'Audio',
  },
  [FILE_TYPE_CATEGORIES.VIDEO]: {
    bg: 'from-slate-800/20 to-indigo-500/10',
    iconFill: 'fill-slate-400',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300',
    label: 'Video',
  },
  [FILE_TYPE_CATEGORIES.ARCHIVE]: {
    bg: 'from-amber-500/10 to-yellow-400/10',
    iconFill: 'fill-amber-500',
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
    label: 'Archive',
  },
  [FILE_TYPE_CATEGORIES.TEXT]: {
    bg: 'from-n-3/30 to-n-2/30',
    iconFill: 'fill-n-4 fill-n-4 dark:fill-n-3',
    badge: 'bg-n-2 text-n-5 dark:bg-n-5/30 dark:text-n-3',
    label: 'Text',
  },
  [FILE_TYPE_CATEGORIES.OTHER]: {
    bg: 'from-n-3/30 to-n-2/30',
    iconFill: 'fill-n-4 dark:fill-n-3',
    badge: 'bg-n-2 text-n-5 dark:bg-n-5/30 dark:text-n-3',
    label: 'File',
  },
}
